/* eslint-disable import/prefer-default-export */
import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import { RootState } from "../../../store";
import { IAuthorizedSigners } from "../../../interfaces/AssessmentInterfaces";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import ServiceProviderAutoSelect from "../../ServiceProvider/ServiceProviderAutoSelect";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";

export function AuthorizedSigners({
  signers,
  onChange,
  spID,
  showTitle = true
}: {
  signers?: IAuthorizedSigners;
  onChange: (v: IAuthorizedSigners) => void;
  spID: number;
  showTitle?: boolean;
}): React.ReactNode {
  const assessmentLabels = useAssessmentLabel();

  // selectors
  const serviceProviders = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  // variables
  const sp1 = signers?.signerSp1?.id || null;
  const sp2 = signers?.signerSp2?.id || null;
  const sp3 = signers?.signerSp3?.id || null;
  const allIds = serviceProviders?.map((sp: ServiceProvider) => Number(sp.id)) || [];

  // helper functions
  const getLimitBy = (currentSp: number | null) => {
    const others = [sp1, sp2, sp3, spID].filter((id) => id && id !== currentSp);
    return allIds.filter((id: number) => !others.includes(id));
  };

  const handleChange = (key: string, value: number | null) => {
    onChange({
      signerSp1: { id: key === "sp1" ? value : sp1 },
      signerSp2: { id: key === "sp2" ? value : sp2 },
      signerSp3: { id: key === "sp3" ? value : sp3 }
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}
    >
      {showTitle && (
        <Typography component="span" fontWeight="600">
          {assessmentLabels.authorizedSigners || "Authorized Signers"}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "280px" } }}>
          <ServiceProviderAutoSelect
            label="Authorized Signer 1"
            isClearable
            disableDefaultError
            serviceProviderId={sp1 || undefined}
            onChange={(val) => handleChange("sp1", val as number | null)}
            limitBy={allIds.length > 0 ? getLimitBy(sp1) : undefined}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", md: "280px" } }}>
          <ServiceProviderAutoSelect
            label="Authorized Signer 2"
            isClearable
            disableDefaultError
            serviceProviderId={sp2 || undefined}
            onChange={(val) => handleChange("sp2", val as number | null)}
            limitBy={allIds.length > 0 ? getLimitBy(sp2) : undefined}
          />
        </Box>

        <Box sx={{ width: { xs: "100%", md: "280px" } }}>
          <ServiceProviderAutoSelect
            label="Authorized Signer 3"
            isClearable
            disableDefaultError
            serviceProviderId={sp3 || undefined}
            onChange={(val) => handleChange("sp3", val as number | null)}
            limitBy={allIds.length > 0 ? getLimitBy(sp3) : undefined}
          />
        </Box>
      </Box>
    </Box>
  );
}
